import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppIcon, AppIcons } from "./AppIcons";

// AppRail - THE NAVIGATION RAIL (the business look, wave B, owner-approved 2026-10-13; picture
// `documentation/mockups/business-look.html`). Chats / Overview / Tasks / Revenue / Sent left the row of tabs above the
// chat list and became ONE `<nav>`:
//   >= 1100px (`AppLayout.WideMin`, the three panes): a 64px column at the far left - a line icon over a small label,
//             the count as a small neutral badge on the icon, the lit item on the accent tint with an accent bar;
//   below:    a bar fixed to the bottom of the window with the same items (56px + the device's safe area).
// The SAME markup either way - `app.css` (block `wac-structure`) lays it out - and the SAME hash routes as the tabs it
// replaces, so `AppKeys` (`g c/t/o`), `AppPalette` and `AppThreeColRules.TabFor` are untouched.
// ⚠ THE OLD HOOKS ARE KEPT: the `<nav>` is still `.tabs` named "Views", an item is still `a.tab` (`.on` +
// `aria-current="page"` when lit) and its count is still its `<i>` - harnesses and suites find them by those.
// ⛔ WHERE IT IS SHOWN is not decided here: `AppMain` draws it on every signed-in archive view and never on sign-up,
// sign-in, leaving, "Import a backup", the Claude panel or the signed-out card (`AppThreeColRules.SideRoutes`).
// ⛔ Every word is escaped; no inline style (the CSP). Navigation only - nothing here can send (decision 9).

/** One item of the rail. `Count` "" = no badge (Overview, Sent). */
export class AppRailItem {
    public Route: string = "";
    public Word: string = "";
    public Count: string = "";
}

export class AppRailView {
    /** On the `<nav>` beside `tabs` - every rail rule in app.css hangs off it. */
    public static readonly NavClass: string = "rail";
    /** The landmark's name (told apart from the chat list's `<nav>` "Chats"). */
    public static readonly Label: string = "Views";
    /** On `<html>` while a rail is on the page (`AppMain` sets it after a paint): the page keeps room for the bottom bar. */
    public static readonly HtmlClass: string = "wac-rail";

    /** DOCUMENTED EXCEPTION: a constructor of a plain value. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Item = (Route: string, Word: string, Count: string): AppRailItem => {
        const Out: AppRailItem = new AppRailItem();
        Out.Route = Route;
        Out.Word = Word;
        Out.Count = Count;
        return Out;
    };

    /** The icon of a route; anything unknown gets the chats bubble (never an empty item). */
    public static IconOf = (Route: string): AppIcon => {
        if (getCoreUtils().KErrorMode === true) { return AppIcons.NavChats; }
        try {
            if (Route === "overview") { return AppIcons.NavOverview; }
            if (Route === "tasks") { return AppIcons.NavTasks; }
            if (Route === "money") { return AppIcons.NavRevenue; }
            if (Route === "sent") { return AppIcons.NavSent; }
            return AppIcons.NavChats;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppRailView.IconOf");
            return AppIcons.NavChats;
        }
    };

    /** One item: the icon (with the count on it), then the word. The lit one says so to a screen reader too. */
    public static ItemHtml = (One: AppRailItem, At: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Cls: string = "tab";
            let Now: string = "";
            if (One.Route === At) { Cls = "tab on"; Now = " aria-current=\"page\""; }
            let Tally: string = "";
            if (nz(One.Count).length > 0) { Tally = "<i>" + kHTMLCore.EscapeHtml(One.Count) + "</i>"; }
            return "<a class=\"" + Cls + "\" href=\"#" + kHTMLCore.EscapeAttr(One.Route) + "\"" + Now + "><span class=\"railico\">"
                + AppIcons.Svg(AppRailView.IconOf(One.Route), 22) + Tally + "</span><span class=\"raillbl\">"
                + kHTMLCore.EscapeHtml(One.Word) + "</span></a>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppRailView.ItemHtml");
            return "";
        }
    };

    /** The rail: a named `<nav>` of the items, `At` lit. */
    public static Html = (Items: AppRailItem[], At: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<nav class=\"tabs " + AppRailView.NavClass + "\" aria-label=\"" + AppRailView.Label + "\">"];
            for (const One of Items) { Out.push(AppRailView.ItemHtml(One, At)); }
            Out.push("</nav>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppRailView.Html");
            return "";
        }
    };
}
